import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector, useStore } from 'react-redux';
import moment from 'moment';
import { Input, Row, Col, Button, Label } from 'reactstrap';

import { getMessage } from "CONFIG/i18n";
import { PAGE_COUNT, SESSION_STATUS, CONFIG } from "UTILS/Constants";
import { sessionActions } from "CONFIG/ActionFactory";
import { getCustomerId } from "COMPONENTS/MainLayout/selectors";
import  toastr  from 'COMPONENTS/ShowTostr/ShowTostr';
import Spinner from "COMPONENTS/Spinner/Spinner";
import { getSelectedLanguage } from 'REDUX/Selector/LanguageSelector';

// Component & hooks
import HeaderContainer from "COMPONENTS/SuperAdmin/Header/HeaderContainer";
import { DateRangePicker, useDateRangePicker } from './DateRangePicker';
import { SessionsInfoTable, SessionsTable } from './Table';
import { Pagination, usePagination } from './Pagination';
import { useLimit } from './useLimit';
import { INIT_SEARCH_STATUS, useSearchQuery } from './useSearchQuery';

import backArrow from "ASSETS/Images/back.svg";
import ic_save_icon from "ASSETS/Images/ic_save_icon.svg";
import ic_save_icon_disable from "ASSETS/Images/ic_save_icon_disable.svg";

import style from './style.module.less';

const startDate = moment().subtract(1, 'day');
const endDate = moment();

const STATUS_OPTIONS = [INIT_SEARCH_STATUS, SESSION_STATUS.ACTIVE, SESSION_STATUS.CLOSED, SESSION_STATUS.ABANDONED];

export const SupportCenterReportContainer = ({
  organisationName,
  showAddUserModal,
  showAddUserDropdown,
  showBulkImportUserModal,
  showAboutOrgModal,
  getAllUserData,
  showQRPopUp,
  showQR,
}) => {

  const store = useStore();

  const logoUrl = useSelector((state) => state.OrganisationReducer.logoUrl);

  const language = useSelector(getSelectedLanguage);

  const { dates, onDatesChange, focusedInput, onFocusChange, existDates, isOutsidePeriod } = useDateRangePicker(startDate, endDate); 

  const isSearchable = useMemo(() => existDates && !isOutsidePeriod, [existDates, isOutsidePeriod]);
  const [isLoading, setIsLoading] = useState(false);
  const canCallApi = useMemo(() => isSearchable && !isLoading, [isSearchable, isLoading]);

  const history = useHistory();

  const backPage = useCallback(() => {
    history.push(CONFIG.path.admin);
  }, [history]);

  const { pageRef, activePage, changePage } = usePagination();

  const {
    searchQuery,
    paramsSearchQuery,
    handleUpdateSearchSessionName,
    handleUpdateSearchOwner,
    handleSearchStatus,
  } = useSearchQuery();

  const [data, setData] = useState({
    aveReactionTime: null,
    aveTalkTime: null,
    incoming: null,
    resCount: null,
    resRate: null,
    total: null,
    sessions: [],
  });

  const supportCenterSessionsInfo = useMemo(() => ({
    total: data.total ?? 0,
    resCount: data.resCount ?? 0,
    resRate: data.resRate ?? 0,
    aveReactionTime: data.aveReactionTime ?? 0,
    aveTalkTime: data.aveTalkTime ?? 0,
  }), [data]);

  const supportCenterSessionsList = useMemo(() => data.sessions, [data]);

  const { limit, limitRef, updateLimit } = useLimit();
  
  const totalItemsCount = useMemo(() => Math.ceil(supportCenterSessionsInfo.total / limit),
    [supportCenterSessionsInfo, limit]);

  const itemsCountPerPage= useMemo(() => Math.ceil(activePage / totalItemsCount), [activePage, totalItemsCount]);

  const handleUpdateLimit = useCallback(async (e) => {
    
    try {
      if (!canCallApi) return;
      setIsLoading(true);
      const nextLimit = e.currentTarget.value;
      const state = store.getState();
      const customerId = getCustomerId(state);
      const { data: { data: resData} } = await sessionActions.getSupportCenterReportAction({
        customerId,
        language,
        from: dates.startDate.startOf("day").toISOString(),
        to: dates.endDate.startOf("day").toISOString(),
        limit: nextLimit,
        page: 0,
        ...paramsSearchQuery,
      });
      updateLimit(nextLimit);
      changePage(0);
      setData(resData);
    } catch (error) {
      toastr.error(getMessage(error.code));
    } finally {
      setIsLoading(false);
    }

  }, [canCallApi, dates, paramsSearchQuery, updateLimit, store, language]);

  const handleChangeActivePage = useCallback(async (nextActivePage) => {
    try {
      if (!canCallApi) return;
      setIsLoading(true);
      const nextPage = nextActivePage -1;
      const state = store.getState();
      const customerId = getCustomerId(state);
      const { data: { data: resData} } = await sessionActions.getSupportCenterReportAction({
        customerId,
        language,
        from: dates.startDate.startOf("day").toISOString(),
        to: dates.endDate.startOf("day").toISOString(),
        limit: limitRef.current,
        page: nextPage,
        ...paramsSearchQuery,
      });
      changePage(nextPage);
      setData(resData);
    } catch (error) {
      toastr.error(getMessage(error.code));
    } finally {
      setIsLoading(false);
    }
  }, [canCallApi, paramsSearchQuery, dates, store, language]);

  const handleSearchSessions = useCallback(async () => {
    try {
      if (!canCallApi) return;
      setIsLoading(true);
      const state = store.getState();
      const customerId = getCustomerId(state);
      const { data: { data: resData } } = await sessionActions.getSupportCenterReportAction({
        customerId,
        language,
        from: dates.startDate.startOf("day").toISOString(),
        to: dates.endDate.startOf("day").toISOString(),
        limit: limitRef.current,
        page: 0,
        ...paramsSearchQuery,
      });
      changePage(0);
      setData(resData);
    } catch (error) {
      toastr.error(getMessage(error.code));
    } finally {
      setIsLoading(false);
    }
  } ,[canCallApi, paramsSearchQuery, dates, store, language]);

  const handleBulkExport = useCallback(async () => {
    try {
      if (!canCallApi) return;
      setIsLoading(true);
      const state = store.getState();
      const customerId = getCustomerId(state);
      const { data: resData } = await sessionActions.getSupportCenterReportAction({
        customerId,
        language,
        from: dates.startDate.startOf("day").toISOString(),
        to: dates.endDate.startOf("day").toISOString(),
        isCsv: true,
        ...paramsSearchQuery,
      });
      const fileText = new Blob([resData], { type: "text/csv" });
      const textFile = window.URL.createObjectURL(fileText);
      const downloadElement = document.createElement("a");
      downloadElement.href = textFile;
      downloadElement.download = "SupportCenterReport.csv";
      downloadElement.click();
    } catch (error) {
      toastr.error(getMessage(error.code));
    } finally {
      setIsLoading(false);
    }
  } ,[canCallApi, paramsSearchQuery, dates, store, language]);

  useEffect(() => {
    const state = store.getState();
    const customerId = getCustomerId(state);
    const mutableLanguage =  getSelectedLanguage(state);
    setIsLoading(true);
    sessionActions.getSupportCenterReportAction({
      customerId,
      language: mutableLanguage,
      from: startDate.startOf("day").toISOString(),
      to: endDate.startOf("day").toISOString(),
      searchStatus: INIT_SEARCH_STATUS,
      limit: limitRef.current,
      page: pageRef.current,
    })
    .then(({ data: { data: resData }}) => {
      setData(resData);
    })
    .catch((error) => {
      toastr.error(getMessage(error.code));
    }).finally(() => {
      setIsLoading(false);
    });
  }, [store]);

  const pageInfo = useMemo(() => {
    const from = (activePage - 1) * limit + 1;
    const to = (activePage - 1) * limit + supportCenterSessionsList.length;
    const totalCount = supportCenterSessionsInfo.total;

    return {
      from,
      to: totalCount <= to ? totalCount : to,
      totalCount,
    }
  }, [activePage, limit, supportCenterSessionsList, supportCenterSessionsInfo]);

  return (
    <>
    <HeaderContainer
      title={organisationName}
      headerButtonText={getMessage("ADD_USER")}
      history={history}
      showAddUserModal={showAddUserModal}
      showAddUserDropdown={showAddUserDropdown}
      showAboutOrgModal={showAboutOrgModal}
      showBulkImportUserModal={showBulkImportUserModal}
      getAllUserData={getAllUserData}
      logoUrl={logoUrl}
      showQRPopUp={showQRPopUp}
      showQR={showQR}
      isDashboard
      isAdmin
    />
    <div className="col-12 d-flex align-items-center backBtn pl-3 pt-2">
      <img 
        src={backArrow} 
        className="pr-2 cursorPointer"
        onClick={backPage} 
      />
      <span 
        className="cursorPointer" 
        onClick={backPage}
      >
        {getMessage("BACK")}
      </span>

    </div>
    <div className={style.container}>   
      <div className={`${style.searchForm} ${language} d-flex align-items-center justify-content-end`}>
        <Label className={`${style.inputContainer} d-flex align-items-center m-0`}>
          {getMessage("SESSION_NAME")}
          <Input 
            type="text"
            placeholder={getMessage("PARTIAL_MATCH_SEARCH")}
            className={style.inputText}
            value={searchQuery.searchSessionName}
            disabled={isLoading}
            onChange={handleUpdateSearchSessionName}
            maxLength={20}
          />
        </Label>
        <Label className={`${style.inputContainer} d-flex align-items-center m-0`}>
          {getMessage("OPERATOR")}
          <Input
            type="text"
            placeholder={getMessage("PARTIAL_MATCH_SEARCH")}
            className={style.inputText}
            value={searchQuery.searchOwner}
            disabled={isLoading}
            onChange={handleUpdateSearchOwner}
            maxLength={20}
          />
        </Label>
        <Label className={`${style.inputContainer} d-flex align-items-center m-0`}>
          {getMessage("STATUS")}
          <Input
            type="select"
            className={style.inputSelect}
            value={searchQuery.searchStatus}
            disabled={isLoading}
            onChange={handleSearchStatus}
          >
            {STATUS_OPTIONS.map((status) => <option key={`status_${status}`} value={status}>{getMessage(status)}</option>)}
          </Input>
        </Label>
        <Label className={`${style.inputContainer} d-flex align-items-center m-0`}>
          {getMessage("AGGREGATE_DURATION")}
          <DateRangePicker 
            className={style.datePicker}
            dates={dates}
            onDatesChange={onDatesChange}
            focusedInput={focusedInput}
            onFocusChange={onFocusChange}
            disabled={isLoading}
          />
        </Label>
        <Button
          className={`customBtn ${style.searchButton}`}
          disabled={!canCallApi}
          onClick={handleSearchSessions}
        >
          {getMessage("SEARCH")}
        </Button>
        <Button className={style.csvBulkExportButton} onClick={handleBulkExport} disabled={!canCallApi}>
          <img
            src={isOutsidePeriod
              ? ic_save_icon_disable
              : ic_save_icon}
            alt={getMessage("SAVE")}
            title={getMessage("SAVE")}
          />
        </Button>

      </div>

      <SessionsInfoTable 
        supportCenterSessionsInfo={supportCenterSessionsInfo}
      />

      <SessionsTable
        supportCenterSessionsList={supportCenterSessionsList}
      />

      <Row className="pt-4 m-0 align-items-center">
        <Col className="p-0">
          {supportCenterSessionsList.length > 0 ? getMessage("PAGINATION_TOTAL_ENTRIES", pageInfo) : null}
        </Col>
        <Col className="p-0">
          {(supportCenterSessionsInfo.total > limit || activePage > 1) && (<Pagination
            disabled={!canCallApi}
            activePage={activePage}
            itemsCountPerPage={itemsCountPerPage}
            totalItemsCount={totalItemsCount}
            pageRangeDisplayed={3}
            onChange={handleChangeActivePage}
          />)}
        </Col>
        <Col className="p-0 d-flex justify-content-end">
          {(supportCenterSessionsInfo.total > limit || activePage > 1) && (
            <Input
              type="select"
              disabled={!canCallApi}
              className={style.select}
              value={limit}
              onChange={handleUpdateLimit}
            >
              {PAGE_COUNT.map(count => <option value={count} key={`page_${count}`}>{count}</option>)}
            </Input>
          )}
        </Col>
      </Row>
    </div>
    <Spinner showSpinner={isLoading} />
    </>
  );
}
